import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import {
  Plus, Trash2, Edit2, Search, Filter,
  Stethoscope, Heart, ShieldCheck,
  CheckCircle2, AlertCircle, Loader2,
  Clock, Calendar, ChevronRight, X,
  HeartHandshake, Layers, IndianRupee,
  Activity, Check, ArrowRight, Tag, Sparkles, FolderPlus,
  Upload, Image as ImageIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const OpheServiceManagement = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Category Modal State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', imageUrl: '', status: 'active' });
  const [submittingCategory, setSubmittingCategory] = useState(false);
  const [uploadingCategoryPhoto, setUploadingCategoryPhoto] = useState(false);
  const categoryFileInputRef = useRef(null);

  // Service Modal State
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    categoryId: '',
    serviceName: '',
    description: '',
    imageUrl: '',
    medicalPoints: [],
    nonMedicalPoints: [],
    pricing: {
      pricingMode: 'both', // 'per_day' | 'hourly_shift' | 'both'
      perDay: '',
      days7: '',
      days15: '',
      days30: '',
      hours12: '',
      hours24: '',
      customHoursRate: ''
    },
    status: 'active'
  });
  const [submittingService, setSubmittingService] = useState(false);
  const [uploadingServicePhoto, setUploadingServicePhoto] = useState(false);
  const serviceFileInputRef = useRef(null);

  // Input states for adding bullet points
  const [medicalInput, setMedicalInput] = useState('');
  const [nonMedicalInput, setNonMedicalInput] = useState('');

  useEffect(() => {
    fetchCatalog();
  }, []);

  const fetchCatalog = async () => {
    try {
      setLoading(true);
      const [catsRes, servsRes] = await Promise.all([
        api.get('/ophe/categories'),
        api.get('/ophe/services')
      ]);
      setCategories(catsRes.data?.data || []);
      setServices(servsRes.data?.data || []);
    } catch (error) {
      toast.error('Failed to load OPHE catalog data');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // 📸 PHOTO UPLOAD HANDLER
  // ==========================================
  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, JPEG, WEBP)');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image size must be less than 8MB');
      return;
    }

    const toastId = toast.loading('Uploading photo...');
    if (type === 'category') setUploadingCategoryPhoto(true);
    if (type === 'service') setUploadingServicePhoto(true);

    try {
      const upData = new FormData();
      upData.append('documents', file);

      const response = await api.post('/upload', upData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.data?.files?.[0]?.url) {
        const url = response.data.data.files[0].url;
        if (type === 'category') {
          setCategoryForm(prev => ({ ...prev, imageUrl: url }));
        } else if (type === 'service') {
          setServiceForm(prev => ({ ...prev, imageUrl: url }));
        }
        toast.success('Photo uploaded successfully', { id: toastId });
      } else {
        toast.error('Failed to retrieve uploaded image URL', { id: toastId });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed', { id: toastId });
    } finally {
      if (type === 'category') setUploadingCategoryPhoto(false);
      if (type === 'service') setUploadingServicePhoto(false);
    }
  };

  // ==========================================
  // 📂 CATEGORY ACTIONS
  // ==========================================
  const handleOpenCategoryModal = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({
        name: cat.name,
        description: cat.description || '',
        imageUrl: cat.imageUrl || '',
        status: cat.status || 'active'
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', imageUrl: '', status: 'active' });
    }
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSubmittingCategory(true);
    try {
      if (editingCategory) {
        await api.patch(`/ophe/categories/${editingCategory._id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await api.post('/ophe/categories', categoryForm);
        toast.success('Category created successfully');
      }
      setShowCategoryModal(false);
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save category');
    } finally {
      setSubmittingCategory(false);
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!isAdmin) {
      toast.error('Only Administrators are authorized to delete categories');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete category "${name}"?`)) return;
    try {
      await api.delete(`/ophe/categories/${id}`);
      toast.success('Category deleted successfully');
      if (selectedCategoryFilter === id) setSelectedCategoryFilter('all');
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete category');
    }
  };

  // ==========================================
  // 🏥 SERVICE ACTIONS
  // ==========================================
  const handleOpenServiceModal = (serv = null) => {
    if (serv) {
      setEditingService(serv);
      setServiceForm({
        categoryId: serv.categoryId?._id || serv.categoryId,
        serviceName: serv.serviceName,
        description: serv.description || '',
        imageUrl: serv.imageUrl || '',
        medicalPoints: serv.medicalPoints || [],
        nonMedicalPoints: serv.nonMedicalPoints || [],
        pricing: {
          pricingMode: serv.pricing?.pricingMode || 'both',
          perDay: serv.pricing?.perDay || '',
          days7: serv.pricing?.days7 || '',
          days15: serv.pricing?.days15 || '',
          days30: serv.pricing?.days30 || '',
          hours12: serv.pricing?.hours12 || '',
          hours24: serv.pricing?.hours24 || '',
          customHoursRate: serv.pricing?.customHoursRate || ''
        },
        status: serv.status || 'active'
      });
    } else {
      setEditingService(null);
      setServiceForm({
        categoryId: categories.length > 0 ? categories[0]._id : '',
        serviceName: '',
        description: '',
        imageUrl: '',
        medicalPoints: [],
        nonMedicalPoints: [],
        pricing: {
          pricingMode: 'both',
          perDay: '',
          days7: '',
          days15: '',
          days30: '',
          hours12: '',
          hours24: '',
          customHoursRate: ''
        },
        status: 'active'
      });
    }
    setMedicalInput('');
    setNonMedicalInput('');
    setShowServiceModal(true);
  };

  // Points handler - Medical
  const handleAddMedicalPoint = () => {
    if (!medicalInput.trim()) return;
    if (serviceForm.medicalPoints.includes(medicalInput.trim())) {
      toast.error('This point already exists');
      return;
    }
    setServiceForm(prev => ({
      ...prev,
      medicalPoints: [...prev.medicalPoints, medicalInput.trim()]
    }));
    setMedicalInput('');
  };

  const handleRemoveMedicalPoint = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      medicalPoints: prev.medicalPoints.filter((_, i) => i !== idx)
    }));
  };

  // Points handler - Non-Medical
  const handleAddNonMedicalPoint = () => {
    if (!nonMedicalInput.trim()) return;
    if (serviceForm.nonMedicalPoints.includes(nonMedicalInput.trim())) {
      toast.error('This point already exists');
      return;
    }
    setServiceForm(prev => ({
      ...prev,
      nonMedicalPoints: [...prev.nonMedicalPoints, nonMedicalInput.trim()]
    }));
    setNonMedicalInput('');
  };

  const handleRemoveNonMedicalPoint = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      nonMedicalPoints: prev.nonMedicalPoints.filter((_, i) => i !== idx)
    }));
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    if (!serviceForm.categoryId) {
      toast.error('Please select a category');
      return;
    }
    if (!serviceForm.serviceName.trim()) {
      toast.error('Service name is required');
      return;
    }

    setSubmittingService(true);
    try {
      const payload = {
        ...serviceForm,
        pricing: {
          pricingMode: serviceForm.pricing.pricingMode || 'both',
          perDay: Number(serviceForm.pricing.perDay) || 0,
          days7: Number(serviceForm.pricing.days7) || 0,
          days15: Number(serviceForm.pricing.days15) || 0,
          days30: Number(serviceForm.pricing.days30) || 0,
          hours12: Number(serviceForm.pricing.hours12) || 0,
          hours24: Number(serviceForm.pricing.hours24) || 0,
          customHoursRate: Number(serviceForm.pricing.customHoursRate) || 0
        }
      };

      if (editingService) {
        await api.patch(`/ophe/services/${editingService._id}`, payload);
        toast.success('OPHE Service updated successfully');
      } else {
        await api.post('/ophe/services', payload);
        toast.success('OPHE Service created successfully');
      }
      setShowServiceModal(false);
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save OPHE service');
    } finally {
      setSubmittingService(false);
    }
  };

  const handleDeleteService = async (id, name) => {
    if (!isAdmin) {
      toast.error('Only Administrators are authorized to delete services');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete service "${name}"?`)) return;
    try {
      await api.delete(`/ophe/services/${id}`);
      toast.success('Service deleted successfully');
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete service');
    }
  };

  // Filtered Services
  const filteredServices = services.filter(serv => {
    const matchesCategory = selectedCategoryFilter === 'all' || 
      (serv.categoryId?._id || serv.categoryId) === selectedCategoryFilter;
    const matchesSearch = !searchQuery || 
      serv.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      serv.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      serv.medicalPoints?.some(p => p.toLowerCase().includes(searchQuery.toLowerCase())) ||
      serv.nonMedicalPoints?.some(p => p.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-6 pb-16 font-sans">
      {/* 🌟 PAGE HEADER */}
      <div className="bg-white rounded-2xl border border-slate-200/90 p-5 sm:p-6 shadow-2xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center shadow-xs">
                <HeartHandshake className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-900 tracking-tight">OPHE Services & Packages</h1>
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  Configure OPHE categories, medical & non-medical offerings, photos, and duration pricing (Day / 7 / 15 / 30 Days).
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => handleOpenCategoryModal()}
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-2xs border border-slate-200"
            >
              <FolderPlus className="w-4 h-4 text-slate-600" />
              <span>+ New Category</span>
            </button>
            <button
              onClick={() => handleOpenServiceModal()}
              disabled={categories.length === 0}
              className="px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-sm disabled:opacity-50"
              title={categories.length === 0 ? 'Create a category first' : 'Create new OPHE service'}
            >
              <Plus className="w-4 h-4" />
              <span>+ New Service Package</span>
            </button>
          </div>
        </div>

        {/* Quick Stats Ribbon */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-5 mt-5 border-t border-slate-100">
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Categories</span>
            <span className="text-lg font-black text-slate-800">{categories.length}</span>
          </div>
          <div className="p-3 bg-teal-50/60 rounded-xl border border-teal-100">
            <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider block">Total Services</span>
            <span className="text-lg font-black text-teal-800">{services.length}</span>
          </div>
          <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider block">Active Packages</span>
            <span className="text-lg font-black text-emerald-800">
              {services.filter(s => s.status === 'active').length}
            </span>
          </div>
          <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100">
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider block">Duration Plans</span>
            <span className="text-xs font-bold text-blue-900 mt-1 block">1D / 7D / 15D / 30D</span>
          </div>
        </div>
      </div>

      {/* 🏷️ CATEGORY FILTER TABS + SEARCH */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          <button
            onClick={() => setSelectedCategoryFilter('all')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              selectedCategoryFilter === 'all'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            All Categories ({services.length})
          </button>
          {categories.map(cat => (
            <div key={cat._id} className="relative group shrink-0">
              <button
                onClick={() => setSelectedCategoryFilter(cat._id)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  selectedCategoryFilter === cat._id
                    ? 'bg-teal-700 text-white shadow-xs'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {cat.imageUrl && (
                  <img src={cat.imageUrl} alt="" className="w-4 h-4 rounded-full object-cover border border-white/40" />
                )}
                <span>{cat.name}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  selectedCategoryFilter === cat._id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {cat.servicesCount || 0}
                </span>
              </button>
            </div>
          ))}
        </div>

        {/* Search Box */}
        <div className="relative min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search service, medical point..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600"
          />
        </div>
      </div>

      {/* 📦 CATEGORIES MANAGER ACCORDION */}
      {categories.length > 0 && (
        <details className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs group">
          <summary className="p-4 flex items-center justify-between cursor-pointer list-none select-none text-xs font-bold text-slate-700 hover:text-slate-900">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-teal-600" />
              <span>Manage Existing Categories ({categories.length})</span>
            </div>
            <span className="text-[11px] text-teal-700 font-semibold group-open:rotate-90 transition-transform">
              <ChevronRight className="w-4 h-4" />
            </span>
          </summary>
          <div className="p-4 pt-0 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            {categories.map(cat => (
              <div key={cat._id} className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {cat.imageUrl ? (
                    <img src={cat.imageUrl} alt={cat.name} className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs shrink-0">
                      <ImageIcon className="w-4 h-4" />
                    </div>
                  )}
                  <div className="min-w-0 truncate">
                    <h4 className="text-xs font-bold text-slate-900 truncate">{cat.name}</h4>
                    <span className="text-[10px] text-slate-500 block truncate">
                      {cat.servicesCount || 0} packages • <span className={cat.status === 'active' ? 'text-emerald-600 font-bold' : 'text-slate-400'}>{cat.status}</span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleOpenCategoryModal(cat)}
                    className="p-1.5 text-slate-500 hover:text-teal-700 hover:bg-white rounded-lg transition-colors cursor-pointer"
                    title="Edit Category"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteCategory(cat._id, cat.name)}
                      className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-white rounded-lg transition-colors cursor-pointer"
                      title="Delete Category (Admin only)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* 🏥 SERVICES LIST */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px] bg-white rounded-2xl border border-slate-200">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
            <span className="text-xs font-bold text-slate-500">Loading OPHE Services Catalog...</span>
          </div>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 p-6">
          <HeartHandshake className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-slate-800">No OPHE Services Found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            {categories.length === 0
              ? 'Start by creating your first OPHE Category, then add service packages with photos, medical & non-medical points.'
              : 'No service packages match your filter. Click "+ New Service Package" to create one.'}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            {categories.length === 0 ? (
              <button
                onClick={() => handleOpenCategoryModal()}
                className="px-4 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 transition-all cursor-pointer"
              >
                Create Category First
              </button>
            ) : (
              <button
                onClick={() => handleOpenServiceModal()}
                className="px-4 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 transition-all cursor-pointer"
              >
                + Add Service Package
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filteredServices.map(serv => (
            <div
              key={serv._id}
              className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between overflow-hidden group"
            >
              {/* Photo Banner Header (if photo is present) */}
              {serv.imageUrl ? (
                <div className="h-44 w-full overflow-hidden bg-slate-100 relative">
                  <img
                    src={serv.imageUrl}
                    alt={serv.serviceName}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-white/90 text-teal-900 backdrop-blur-xs">
                      {serv.categoryId?.name || 'Category'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase ${
                      serv.status === 'active' ? 'bg-emerald-500 text-white' : 'bg-slate-500 text-white'
                    }`}>
                      {serv.status}
                    </span>
                  </div>
                </div>
              ) : null}

              {/* Card Header Body */}
              <div className="p-5 border-b border-slate-100 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {!serv.imageUrl && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-50 text-teal-800 border border-teal-200 mb-1.5">
                        {serv.categoryId?.name || 'General Category'}
                      </span>
                    )}
                    <h3 className="text-base font-black text-slate-900">{serv.serviceName}</h3>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleOpenServiceModal(serv)}
                      className="p-1.5 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                      title="Edit Service"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteService(serv._id, serv.serviceName)}
                        className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                        title="Delete Service (Admin only)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {serv.description && (
                  <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                    {serv.description}
                  </p>
                )}
              </div>

              {/* 🩺 Medical & 🏠 Non-Medical Inclusions Blocks */}
              <div className="p-5 space-y-4">
                {/* 1. Medical Inclusions */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                      <Stethoscope className="w-3.5 h-3.5 text-teal-600" />
                      Medical Inclusions ({serv.medicalPoints?.length || 0})
                    </span>
                  </div>
                  {serv.medicalPoints && serv.medicalPoints.length > 0 ? (
                    <ul className="space-y-1.5 bg-teal-50/30 p-3 rounded-xl border border-teal-100/60">
                      {serv.medicalPoints.map((pt, idx) => (
                        <li key={idx} className="text-xs text-slate-700 flex items-start gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />
                          <span className="font-medium leading-snug">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic pl-1">No medical points specified.</p>
                  )}
                </div>

                {/* 2. Non-Medical Inclusions */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                      <Heart className="w-3.5 h-3.5 text-violet-600" />
                      Non-Medical Inclusions ({serv.nonMedicalPoints?.length || 0})
                    </span>
                  </div>
                  {serv.nonMedicalPoints && serv.nonMedicalPoints.length > 0 ? (
                    <ul className="space-y-1.5 bg-violet-50/30 p-3 rounded-xl border border-violet-100/60">
                      {serv.nonMedicalPoints.map((pt, idx) => (
                        <li key={idx} className="text-xs text-slate-700 flex items-start gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-violet-600 shrink-0 mt-0.5" />
                          <span className="font-medium leading-snug">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic pl-1">No non-medical points specified.</p>
                  )}
                </div>
              </div>

              {/* 💰 DURATION PRICING RIBBON */}
              <div className="bg-slate-50/80 p-4 border-t border-slate-100 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <IndianRupee className="w-3 h-3 text-teal-600" />
                    Duration & Shift Pricing Plans
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200/70 text-slate-700 capitalize">
                    {serv.pricing?.pricingMode === 'hourly_shift' ? 'Hourly / Shift' : serv.pricing?.pricingMode === 'per_day' ? 'Per Day / Multi-Day' : 'Daily & Shift'}
                  </span>
                </div>

                {/* Shift / Hourly Rates if available */}
                {(serv.pricing?.pricingMode !== 'per_day' || serv.pricing?.hours12 || serv.pricing?.hours24) && (
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    <div className="bg-white p-1.5 rounded-lg border border-teal-200/60 shadow-2xs">
                      <span className="text-[9px] text-teal-700 block font-bold">12h Shift</span>
                      <span className="text-xs font-black text-slate-900">
                        ₹{serv.pricing?.hours12?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-indigo-200/60 shadow-2xs">
                      <span className="text-[9px] text-indigo-700 block font-bold">24h Shift</span>
                      <span className="text-xs font-black text-indigo-950">
                        ₹{serv.pricing?.hours24?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">Custom/Hr</span>
                      <span className="text-xs font-black text-slate-800">
                        ₹{serv.pricing?.customHoursRate?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Daily & Multi-Day Rates if available */}
                {(serv.pricing?.pricingMode !== 'hourly_shift' || serv.pricing?.perDay || serv.pricing?.days7) && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-center">
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">Per Day</span>
                      <span className="text-xs font-black text-slate-900">
                        ₹{serv.pricing?.perDay?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">7 Days</span>
                      <span className="text-xs font-black text-teal-800">
                        ₹{serv.pricing?.days7?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">15 Days</span>
                      <span className="text-xs font-black text-blue-800">
                        ₹{serv.pricing?.days15?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">30 Days</span>
                      <span className="text-xs font-black text-emerald-800">
                        ₹{serv.pricing?.days30?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ==========================================
          📂 MODAL 1: CATEGORY CREATE / EDIT
         ========================================== */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderPlus className="w-5 h-5 text-teal-600" />
                <h3 className="text-sm font-black text-slate-900">
                  {editingCategory ? 'Edit OPHE Category' : 'Create OPHE Category'}
                </h3>
              </div>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveCategory} className="p-6 space-y-4">
              {/* Category Name */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Category Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Elderly Care, Critical Nursing Care"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                />
              </div>

              {/* Category Photo / Image Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-teal-600" />
                  Category Photo / Icon
                </label>
                <div className="flex items-center gap-3">
                  {categoryForm.imageUrl ? (
                    <div className="relative group w-14 h-14 rounded-xl overflow-hidden border border-slate-200 shrink-0">
                      <img src={categoryForm.imageUrl} alt="Category" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setCategoryForm({ ...categoryForm, imageUrl: '' })}
                        className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Remove photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400 shrink-0">
                      <ImageIcon className="w-5 h-5" />
                    </div>
                  )}

                  <div className="flex-1 space-y-1">
                    <input
                      type="file"
                      ref={categoryFileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'category')}
                    />
                    <button
                      type="button"
                      disabled={uploadingCategoryPhoto}
                      onClick={() => categoryFileInputRef.current?.click()}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      {uploadingCategoryPhoto ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5 text-slate-600" />
                      )}
                      <span>{categoryForm.imageUrl ? 'Change Photo' : 'Upload Photo'}</span>
                    </button>
                    <input
                      type="text"
                      placeholder="Or paste image URL"
                      value={categoryForm.imageUrl}
                      onChange={(e) => setCategoryForm({ ...categoryForm, imageUrl: e.target.value })}
                      className="w-full px-2.5 py-1 text-[11px] border border-slate-200 rounded-lg focus:outline-none focus:border-teal-600"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Description (Optional)
                </label>
                <textarea
                  rows="2"
                  placeholder="Brief description of services falling under this category..."
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600 resize-none"
                />
              </div>

              {/* Status */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Status</label>
                <select
                  value={categoryForm.status}
                  onChange={(e) => setCategoryForm({ ...categoryForm, status: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingCategory || uploadingCategoryPhoto}
                  className="px-4 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer shadow-sm"
                >
                  {submittingCategory && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  <span>{editingCategory ? 'Update Category' : 'Save Category'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==========================================
          🏥 MODAL 2: SERVICE (SUB-CATEGORY) CREATE / EDIT
         ========================================== */}
      {showServiceModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-150 my-8">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HeartHandshake className="w-5 h-5 text-teal-600" />
                <div>
                  <h3 className="text-sm font-black text-slate-900">
                    {editingService ? 'Edit OPHE Service Package' : 'Create OPHE Service Package'}
                  </h3>
                  <p className="text-[10px] text-slate-500">Configure photo, medical & non-medical points, and duration pricing</p>
                </div>
              </div>
              <button
                onClick={() => setShowServiceModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveService} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto no-scrollbar">
              {/* Category & Service Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Select Category <span className="text-rose-500">*</span>
                  </label>
                  <select
                    required
                    value={serviceForm.categoryId}
                    onChange={(e) => setServiceForm({ ...serviceForm, categoryId: e.target.value })}
                    className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-teal-600"
                  >
                    <option value="">-- Choose Category --</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Service / Sub-Category Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 24/7 ICU Trained Nurse at Home"
                    value={serviceForm.serviceName}
                    onChange={(e) => setServiceForm({ ...serviceForm, serviceName: e.target.value })}
                    className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                  />
                </div>
              </div>

              {/* Service Photo / Image Upload Field */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <ImageIcon className="w-4 h-4 text-teal-600" />
                  Service Photo / Banner Image
                </label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  {serviceForm.imageUrl ? (
                    <div className="relative group w-32 h-20 rounded-xl overflow-hidden border border-slate-200 shrink-0">
                      <img src={serviceForm.imageUrl} alt="Service Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setServiceForm({ ...serviceForm, imageUrl: '' })}
                        className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Remove photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-20 rounded-xl bg-white border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 shrink-0">
                      <ImageIcon className="w-6 h-6 mb-1 text-slate-300" />
                      <span className="text-[10px] font-bold">No Photo</span>
                    </div>
                  )}

                  <div className="flex-1 space-y-1.5 w-full">
                    <input
                      type="file"
                      ref={serviceFileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'service')}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={uploadingServicePhoto}
                        onClick={() => serviceFileInputRef.current?.click()}
                        className="px-3.5 py-1.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow-2xs"
                      >
                        {uploadingServicePhoto ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Upload className="w-3.5 h-3.5 text-teal-600" />
                        )}
                        <span>{serviceForm.imageUrl ? 'Change Photo' : 'Upload Service Photo'}</span>
                      </button>
                      <span className="text-[10px] text-slate-400">PNG, JPG, WEBP (Max 8MB)</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Or paste external image URL (https://...)"
                      value={serviceForm.imageUrl}
                      onChange={(e) => setServiceForm({ ...serviceForm, imageUrl: e.target.value })}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-teal-600"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Service Description</label>
                <textarea
                  rows="2"
                  placeholder="Provide an overview of this OPHE care package..."
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600 resize-none"
                />
              </div>

              {/* 🩺 1. Medical Inclusions Points */}
              <div className="p-4 bg-teal-50/50 rounded-xl border border-teal-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-black uppercase tracking-wider text-teal-900 flex items-center gap-1.5">
                    <Stethoscope className="w-4 h-4 text-teal-700" />
                    Medical Inclusions (Medical Points)
                  </label>
                  <span className="text-[10px] font-bold text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full">
                    {serviceForm.medicalPoints.length} added
                  </span>
                </div>

                {/* Add Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g., Vital monitoring (BP, Sugar, Pulse), Medication administration"
                    value={medicalInput}
                    onChange={(e) => setMedicalInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMedicalPoint(); } }}
                    className="flex-1 px-3.5 py-1.5 bg-white border border-teal-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-700"
                  />
                  <button
                    type="button"
                    onClick={handleAddMedicalPoint}
                    className="px-3 py-1.5 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 transition-all cursor-pointer shrink-0"
                  >
                    + Add
                  </button>
                </div>

                {/* Points List */}
                {serviceForm.medicalPoints.length > 0 && (
                  <div className="space-y-1.5 max-h-36 overflow-y-auto no-scrollbar pt-1">
                    {serviceForm.medicalPoints.map((pt, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-teal-200/70 text-xs">
                        <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5 text-teal-600" />
                          {pt}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveMedicalPoint(idx)}
                          className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 🏠 2. Non-Medical Inclusions Points */}
              <div className="p-4 bg-violet-50/50 rounded-xl border border-violet-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-black uppercase tracking-wider text-violet-900 flex items-center gap-1.5">
                    <Heart className="w-4 h-4 text-violet-700" />
                    Non-Medical Inclusions (Care & Attendant Points)
                  </label>
                  <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
                    {serviceForm.nonMedicalPoints.length} added
                  </span>
                </div>

                {/* Add Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g., Personal hygiene & bathing, Feeding assistance, Mobility support"
                    value={nonMedicalInput}
                    onChange={(e) => setNonMedicalInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNonMedicalPoint(); } }}
                    className="flex-1 px-3.5 py-1.5 bg-white border border-violet-200 rounded-xl text-xs font-medium focus:outline-none focus:border-violet-700"
                  />
                  <button
                    type="button"
                    onClick={handleAddNonMedicalPoint}
                    className="px-3 py-1.5 bg-violet-700 text-white rounded-xl text-xs font-bold hover:bg-violet-800 transition-all cursor-pointer shrink-0"
                  >
                    + Add
                  </button>
                </div>

                {/* Points List */}
                {serviceForm.nonMedicalPoints.length > 0 && (
                  <div className="space-y-1.5 max-h-36 overflow-y-auto no-scrollbar pt-1">
                    {serviceForm.nonMedicalPoints.map((pt, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-violet-200/70 text-xs">
                        <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5 text-violet-600" />
                          {pt}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveNonMedicalPoint(idx)}
                          className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 💰 3. Duration & Shift-Based Pricing Grid */}
              <div className="p-4 bg-slate-50/90 rounded-2xl border border-slate-200/80 space-y-4 shadow-2xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-slate-200/60">
                  <div>
                    <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                      <IndianRupee className="w-4 h-4 text-emerald-600" />
                      Duration Pricing Configuration
                    </label>
                    <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                      Choose pricing model: Per Day rates, Shift/Hourly rates (12h & 24h default + custom), or both.
                    </p>
                  </div>

                  {/* Mode Selector Tabs */}
                  <div className="inline-flex p-1 bg-white rounded-xl border border-slate-200/80 shadow-2xs shrink-0">
                    <button
                      type="button"
                      onClick={() => setServiceForm({
                        ...serviceForm,
                        pricing: { ...serviceForm.pricing, pricingMode: 'per_day' }
                      })}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                        serviceForm.pricing.pricingMode === 'per_day'
                          ? 'bg-teal-700 text-white shadow-xs'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      Per Day
                    </button>
                    <button
                      type="button"
                      onClick={() => setServiceForm({
                        ...serviceForm,
                        pricing: { ...serviceForm.pricing, pricingMode: 'hourly_shift' }
                      })}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                        serviceForm.pricing.pricingMode === 'hourly_shift'
                          ? 'bg-teal-700 text-white shadow-xs'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      Hourly / Shift
                    </button>
                    <button
                      type="button"
                      onClick={() => setServiceForm({
                        ...serviceForm,
                        pricing: { ...serviceForm.pricing, pricingMode: 'both' }
                      })}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                        serviceForm.pricing.pricingMode === 'both'
                          ? 'bg-teal-700 text-white shadow-xs'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      Both Options
                    </button>
                  </div>
                </div>

                {/* ⏱️ Shift & Hourly Pricing (12h, 24h, Custom) */}
                {(serviceForm.pricing.pricingMode === 'hourly_shift' || serviceForm.pricing.pricingMode === 'both') && (
                  <div className="space-y-2">
                    <span className="text-[11px] font-black uppercase tracking-wider text-teal-800 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-teal-600" />
                      Shift & Hourly Rates (12h & 24h Shifts + Custom)
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="bg-white p-3 rounded-xl border border-teal-200/70 shadow-2xs">
                        <label className="text-[11px] font-bold text-teal-900 block mb-1">
                          12 Hours Shift Rate (₹) <span className="text-[10px] text-teal-600 font-normal">(Default 12h)</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g. 800"
                          value={serviceForm.pricing.hours12}
                          onChange={(e) => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, hours12: e.target.value }
                          })}
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-teal-600 focus:bg-white"
                        />
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-indigo-200/70 shadow-2xs">
                        <label className="text-[11px] font-bold text-indigo-900 block mb-1">
                          24 Hours Shift Rate (₹) <span className="text-[10px] text-indigo-600 font-normal">(Default 24h)</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g. 1500"
                          value={serviceForm.pricing.hours24}
                          onChange={(e) => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, hours24: e.target.value }
                          })}
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-indigo-950 focus:outline-none focus:border-indigo-600 focus:bg-white"
                        />
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                        <label className="text-[11px] font-bold text-slate-700 block mb-1">
                          Custom Shift / Per Hour (₹) <span className="text-[10px] text-slate-400 font-normal">(Custom Rate)</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g. 100"
                          value={serviceForm.pricing.customHoursRate}
                          onChange={(e) => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, customHoursRate: e.target.value }
                          })}
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-teal-600 focus:bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* 📅 Per Day & Multi-Day Package Rates */}
                {(serviceForm.pricing.pricingMode === 'per_day' || serviceForm.pricing.pricingMode === 'both') && (
                  <div className="space-y-2">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-600" />
                      Daily & Multi-Day Package Rates
                    </span>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                        <label className="text-[11px] font-bold text-slate-600 block mb-1">Per Day (₹)</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g. 1200"
                          value={serviceForm.pricing.perDay}
                          onChange={(e) => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, perDay: e.target.value }
                          })}
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-teal-600 focus:bg-white"
                        />
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                        <label className="text-[11px] font-bold text-teal-800 block mb-1">7 Days (₹)</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g. 7500"
                          value={serviceForm.pricing.days7}
                          onChange={(e) => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, days7: e.target.value }
                          })}
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-teal-800 focus:outline-none focus:border-teal-600 focus:bg-white"
                        />
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                        <label className="text-[11px] font-bold text-blue-800 block mb-1">15 Days (₹)</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g. 14000"
                          value={serviceForm.pricing.days15}
                          onChange={(e) => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, days15: e.target.value }
                          })}
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-blue-800 focus:outline-none focus:border-teal-600 focus:bg-white"
                        />
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                        <label className="text-[11px] font-bold text-emerald-800 block mb-1">30 Days (₹)</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g. 25000"
                          value={serviceForm.pricing.days30}
                          onChange={(e) => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, days30: e.target.value }
                          })}
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-emerald-800 focus:outline-none focus:border-teal-600 focus:bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Package Status</label>
                <select
                  value={serviceForm.status}
                  onChange={(e) => setServiceForm({ ...serviceForm, status: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowServiceModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingService || uploadingServicePhoto}
                  className="px-5 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer shadow-sm"
                >
                  {submittingService && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  <span>{editingService ? 'Update Service Package' : 'Save Service Package'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpheServiceManagement;
